import React from 'react';
import ModalLoader from '../modal/modal_loader';
import axios from '../axios';

// 作り直した入口画面（entry.js）と同じ色・影
const C = {
  bg: '#F6F6F6',
  surface: '#FFFFFF',
  ink: '#333333',
  inkSoft: '#666666',
  muted: '#8B9097',
  line: '#ECECEE',
  primary: '#FF6467',
  wash: '#FFEAEA',
};

const CARD_SHADOW =
  '0 1px 2px rgba(30,30,40,.03), 0 10px 26px -16px rgba(30,30,40,.14)';

const STYLES = `
.tg-history{position:fixed;top:60px;left:0;right:0;bottom:0;z-index:1010;background:${C.bg};overflow-y:auto;-webkit-overflow-scrolling:touch;padding:16px 18px calc(48px + env(safe-area-inset-bottom));}
.tg-history-col{max-width:800px;margin:0 auto;}
.tg-history-row + .tg-history-row{border-top:1px solid ${C.line};}
.tg-history-title{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;}
.tg-history-input::placeholder{color:${C.muted};opacity:1;font-weight:400;}
.tg-history-input::-webkit-search-cancel-button{-webkit-appearance:none;display:none;}
.tg-history-search:focus-within{border-color:rgba(255,100,103,.55)!important;}
.tg-history button:focus,.tg-history input:focus{outline:none;}
.tg-history button:focus-visible{outline:3px solid rgba(255,100,103,.25);outline-offset:2px;}
.tg-history-more:hover{border-color:#8A8A93!important;color:#8A8A93!important;background:#FAFAFB!important;}
.tg-history-copy:hover{background:${C.wash}!important;}
@media (max-width:559px){
  .tg-history-copy{width:calc(100% - 94px);margin-left:94px;}
}
@media (min-width:860px){
  .tg-history{padding:28px 24px 64px;}
}
`;

const NO_IMAGE = '/images/ja2/img_noimage.gif';

// 最初に見せる件数（これより多いときだけ「すべて表示」を出す）
const INITIAL_COUNT = 10;

// 他の画面とそろえて日付は yyyy/mm/dd で見せる（「2026年9月27日(日) 8:00」→「2026/09/27」）
const slashDate = (text) => {
  const m = /(\d{4})年(\d{1,2})月(\d{1,2})日/.exec(text || '');
  if (!m) return '';
  return `${m[1]}/${m[2].padStart(2, '0')}/${m[3].padStart(2, '0')}`;
};

const S = {
  back: {
    background: 'none',
    border: 'none',
    fontSize: 14,
    color: C.inkSoft,
    fontWeight: 600,
    padding: '6px 8px',
    marginLeft: -8,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
  },
  headline: {
    fontSize: 22,
    fontWeight: 800,
    color: C.primary,
    lineHeight: 1.35,
    letterSpacing: '-.01em',
    margin: '14px 0 20px',
  },
  search: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: C.surface,
    border: `1px solid ${C.line}`,
    borderRadius: 12,
    padding: '0 14px',
    height: 48,
    boxShadow: CARD_SHADOW,
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    padding: 0,
    fontSize: 16,
    color: C.ink,
  },
  clear: {
    flex: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 20,
    height: 20,
    padding: 0,
    border: 'none',
    borderRadius: 999,
    background: C.muted,
    cursor: 'pointer',
  },
  count: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    fontWeight: 400,
    color: '#4A4A52',
    margin: '24px 2px 10px',
  },
  // お好み条件の設定「選択中」と同じく、数はまるの中に入れる
  countBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 20,
    height: 20,
    padding: '0 5px',
    borderRadius: 999,
    background: '#E8E8EB',
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1,
    color: '#4A4A52',
  },
  list: {
    background: C.surface,
    border: `1px solid ${C.line}`,
    borderRadius: 16,
    boxShadow: CARD_SHADOW,
    overflow: 'hidden',
  },
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '10px 14px',
    padding: '14px 16px',
  },
  thumb: {
    flex: 'none',
    width: 80,
    height: 60,
    borderRadius: 10,
    objectFit: 'cover',
    background: C.bg,
    border: `1px solid ${C.line}`,
  },
  date: {
    fontSize: 12,
    color: C.muted,
    fontVariantNumeric: 'tabular-nums',
    margin: '0 0 4px',
  },
  title: {
    fontSize: 15,
    fontWeight: 700,
    color: C.ink,
    lineHeight: 1.5,
    margin: 0,
    wordBreak: 'break-all',
  },
  copy: {
    flex: 'none',
    background: C.surface,
    border: `1px solid ${C.primary}`,
    borderRadius: 8,
    color: C.primary,
    fontSize: 13,
    fontWeight: 700,
    padding: '0 16px',
    height: 36,
    cursor: 'pointer',
  },
  // 一覧のカードの最後に置く
  moreWrap: {
    padding: '4px 16px 16px',
  },
  // お好み条件の設定の「すべて表示」と同じ（白地・うすいグレーの枠・グレーの文字・アイコンなし）
  more: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: 44,
    padding: '4px 16px',
    border: '1px solid #D3D3D8',
    borderRadius: 10,
    background: '#FFFFFF',
    fontSize: 15,
    fontWeight: 400,
    lineHeight: 1.5,
    color: '#D3D3D8',
    cursor: 'pointer',
  },
  empty: {
    background: C.surface,
    border: `1px solid ${C.line}`,
    borderRadius: 16,
    boxShadow: CARD_SHADOW,
    padding: '32px 20px',
    textAlign: 'center',
  },
  emptyTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: C.ink,
    lineHeight: 1.6,
    margin: '0 0 6px',
    wordBreak: 'break-all',
  },
  nowrapChunk: { display: 'inline-block', maxWidth: '100%' },
  emptyNote: { fontSize: 13, color: C.inkSoft, margin: 0 },
};

const History = ({
  props,
  events,
  setHistoryEventId,
  setEvent,
  setEventsPlans,
  setContents,
  setBodyText,
  setTitleText,
  onBack,
  onCopied,
}) => {
  const [keyword, setKeyword] = React.useState('');
  const modalLoaderRef = React.useRef(null);

  const filteredEvents = React.useMemo(() => {
    const trimmed = keyword.trim().toLowerCase();
    if (!trimmed) return events;
    return events.filter(
      (event) => event.title && event.title.toLowerCase().includes(trimmed),
    );
  }, [events, keyword]);
  const closeLoader = () => {
    if (modalLoaderRef.current) modalLoaderRef.current.closeModal();
  };
  const copyEvent = React.useCallback(
    (historyEventId) => {
      modalLoaderRef.current.openModal();
      const body = JSON.stringify({
        event_id: props.event.id,
        circle_id: props.circle.id,
        history_event_id: historyEventId,
      });
      axios
        .post('/api/event/replace_event_from_history', body)
        .then((response) => {
          const copiedEvent = JSON.parse(response.data.event);
          setHistoryEventId(historyEventId);
          setEvent((prev) => ({ ...prev, ...copiedEvent }));
          setTitleText(copiedEvent.title || '');
          setEventsPlans(JSON.parse(response.data.events_plans));
          const contents = JSON.parse(response.data.contents);
          setContents(contents);
          const textContent = contents.find(
            (content) => content.content_type === 1,
          );
          setBodyText(textContent ? textContent.body : '');
          // @context TGW-2677 onCopied() で一覧画面ごと ModalLoader が消えると、後から閉じようとしても
          //   ref が null で閉じる処理が飛ばされ、body の position: fixed が残ってスクロールできなくなる。
          //   画面を切り替える前に閉じる（9/15 以前の「閉じる → 遷移」の順に戻す）。
          closeLoader();
          onCopied();
        })
        .catch(() => {
          closeLoader();
        });
    },
    [
      onCopied,
      props.circle.id,
      props.event.id,
      setBodyText,
      setContents,
      setEvent,
      setEventsPlans,
      setHistoryEventId,
      setTitleText,
    ],
  );

  const isSearching = keyword.trim() !== '';

  // 管理番号153-3: 11件以上あるときは最初の10件だけ見せ、「すべて表示」で残りを開く（検索中は全件から表示）
  const [isShowingAll, setIsShowingAll] = React.useState(false);
  const hasMore = !isSearching && !isShowingAll && filteredEvents.length > INITIAL_COUNT;
  const visibleEvents = hasMore ? filteredEvents.slice(0, INITIAL_COUNT) : filteredEvents;

  // スマホではヘッダーの上に「アプリで開く」の帯が出て高さが変わるため、ヘッダーの下端を測ってその真下から始める
  const [headerBottom, setHeaderBottom] = React.useState(60);
  React.useLayoutEffect(() => {
    const header = document.getElementById('header');
    if (!header) return undefined;
    const measure = () =>
      setHeaderBottom(Math.max(0, Math.round(header.getBoundingClientRect().bottom)));
    measure();
    window.addEventListener('resize', measure);
    const observer = window.ResizeObserver ? new ResizeObserver(measure) : null;
    if (observer) {
      observer.observe(header);
      if (header.previousElementSibling) observer.observe(header.previousElementSibling);
    }
    return () => {
      window.removeEventListener('resize', measure);
      if (observer) observer.disconnect();
    };
  }, []);

  return (
    // サイトのヘッダーの下に重ね、一覧はこの中でスクロールする
    <div className="tg-history" style={{ top: headerBottom }}>
      <style>{STYLES}</style>
      <div className="tg-history-col">
        <button type="button" onClick={onBack} style={S.back}>
          <span style={{ fontSize: 21, lineHeight: 1 }}>‹</span>戻る
        </button>

        <h1 style={S.headline}>過去のイベントをもとに作成</h1>

        <div className="tg-history-search" style={S.search}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <input
            type="search"
            className="tg-history-input"
            aria-label="イベント名で検索"
            placeholder="イベント名で検索"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            style={S.searchInput}
          />
          {keyword !== '' && (
            <button
              type="button"
              aria-label="検索ワードを消す"
              onClick={() => setKeyword('')}
              style={S.clear}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="#FFFFFF" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                <path d="M2 2l6 6M8 2l-6 6" />
              </svg>
            </button>
          )}
        </div>

        <p style={S.count}>
          {isSearching ? '検索結果' : '過去のイベント'}
          <span style={S.countBadge}>{filteredEvents.length}</span>
        </p>

        {filteredEvents.length > 0 ? (
          <React.Fragment>
            <div style={S.list}>
              {visibleEvents.map((event) => (
                <div className="tg-history-row" style={S.row} key={event.id}>
                  <img
                    src={event.main_image_url_thumb || NO_IMAGE}
                    alt=""
                    loading="lazy"
                    style={S.thumb}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={S.date}>{slashDate(event.event_date_include_year) || '日時未定'}</p>
                    <p className="tg-history-title" style={S.title}>
                      {event.title}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="tg-history-copy"
                    style={S.copy}
                    onClick={() => copyEvent(event.id)}
                  >
                    このイベントを使う
                  </button>
                </div>
              ))}
              {hasMore && (
                <div style={S.moreWrap}>
                  <button
                    type="button"
                    className="tg-history-more"
                    style={S.more}
                    onClick={() => setIsShowingAll(true)}
                  >
                    すべて表示
                  </button>
                </div>
              )}
            </div>
          </React.Fragment>
        ) : (
          <div style={S.empty}>
            {/* 1行に収まれば1行、収まらないときだけ「一致する」の後ろで折り返す */}
            <p style={S.emptyTitle}>
              <span style={S.nowrapChunk}>「{keyword.trim()}」に一致する</span>
              <span style={S.nowrapChunk}>イベントはありません</span>
            </p>
            <p style={S.emptyNote}>別のことばで検索してみてください</p>
          </div>
        )}
      </div>
      <ModalLoader ref={modalLoaderRef} />
    </div>
  );
};

export default History;
